import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { tradingService } from '../../../services/TradingService';
import {
  PositionSnapshot,
  PositionStateSnapshot,
  StrategyLegLotSnapshot,
  StrategyPositionSnapshot,
} from '../../../types/trading';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import { normalizeSymbol } from '../../../components/trading-dashboard/portfolioValue';
import compactStyles from '../../../components/trading/CompactTable.module.css';
import { SymbolLogo } from '../../../components/common/SymbolLogo';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { WIDGET_DRAG_HANDLE_CLASS } from '../../../components/widget-shell/WidgetShell';

/* ── Helpers ───────────────────────────────────────────────────────── */

const safeParseTime = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const fmtCurrency = (value: number | null | undefined, withSign = false): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const rendered = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!withSign) return rendered;
  if (value > 0) return `+${rendered}`;
  if (value < 0) return `-${rendered}`;
  return rendered;
};

const fmtQty = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const fmtTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const pnlClass = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  if (value > 0) return compactStyles.pnlPositive;
  if (value < 0) return compactStyles.pnlNegative;
  return '';
};

type NormalHistoricalRow = {
  key: string;
  tradedAt: string;
  symbol: string;
  costToTrade: number;
  qty: number;
  realizedPnl: number;
};

type StrategyChildHistoricalRow = {
  key: string;
  tradedAt: string;
  symbol: string;
  costToTrade: number | null;
  qty: number;
  realizedPnl: number;
  legId: string;
};

type StrategyHistoricalGroup = {
  strategyPositionId: string;
  strategyName: string;
  tradedAt: string;
  costToTrade: number;
  qty: number;
  realizedPnl: number;
  children: StrategyChildHistoricalRow[];
};

const buildNormalRows = (positions: PositionSnapshot[]): NormalHistoricalRow[] =>
  positions
    .filter((position) => position.closed_qty > 0)
    .map((position) => {
      const symbol = normalizeSymbol(position.symbol) || position.symbol;
      const qty = Math.abs(position.closed_qty);
      return {
        key: position.lot_id,
        tradedAt: position.closed_at ?? position.updated_at ?? position.trade_at,
        symbol,
        costToTrade: qty * Math.abs(position.avg_price),
        qty,
        realizedPnl: toFiniteNumber(position.realized_pnl) ?? 0,
      };
    })
    .sort((a, b) => {
      const diff = safeParseTime(b.tradedAt) - safeParseTime(a.tradedAt);
      return diff !== 0 ? diff : a.symbol.localeCompare(b.symbol);
    });

const buildStrategyGroups = (
  strategies: StrategyPositionSnapshot[],
): StrategyHistoricalGroup[] =>
  strategies
    .map((strategy) => {
      const children: StrategyChildHistoricalRow[] = [];
      strategy.legs.forEach((leg) => {
        const normalizedSymbol = normalizeSymbol(leg.symbol) || leg.symbol || '--';
        const legLots = Array.isArray(leg.lots)
          ? (leg.lots as StrategyLegLotSnapshot[])
          : [];
        let appendedLotRow = false;

        legLots.forEach((lot) => {
          const closedQtyRaw = toFiniteNumber(lot.closed_qty);
          const closedQty = closedQtyRaw != null ? Math.abs(closedQtyRaw) : 0;
          if (closedQty <= 0) return;

          const avgPrice = Math.abs(toFiniteNumber(lot.avg_price) ?? 0);
          const realizedPnl = toFiniteNumber(lot.realized_pnl) ?? 0;
          children.push({
            key: `${strategy.strategy_position_id}:${leg.leg_id}:${lot.lot_id}`,
            tradedAt:
              lot.closed_at ??
              lot.updated_at ??
              lot.trade_at ??
              strategy.closed_at ??
              strategy.updated_at ??
              strategy.created_at,
            symbol: normalizedSymbol,
            costToTrade: closedQty * avgPrice,
            qty: closedQty,
            realizedPnl,
            legId: leg.leg_id,
          });
          appendedLotRow = true;
        });

        if (appendedLotRow) return;

        const fallbackRealized = toFiniteNumber(leg.realized_pnl) ?? 0;
        const fallbackClosedQty = Math.abs(toFiniteNumber(leg.closed_qty) ?? 0);
        if (fallbackRealized === 0 && fallbackClosedQty === 0) return;

        const avgPrice = Math.abs(toFiniteNumber(leg.avg_price) ?? 0);
        children.push({
          key: `${strategy.strategy_position_id}:${leg.leg_id}:aggregate`,
          tradedAt:
            leg.updated_at ??
            leg.trade_at ??
            strategy.closed_at ??
            strategy.updated_at ??
            strategy.created_at,
          symbol: normalizedSymbol,
          costToTrade: fallbackClosedQty > 0 ? fallbackClosedQty * avgPrice : null,
          qty: fallbackClosedQty,
          realizedPnl: fallbackRealized,
          legId: leg.leg_id,
        });
      });

      children.sort((a, b) => {
        const diff = safeParseTime(b.tradedAt) - safeParseTime(a.tradedAt);
        return diff !== 0 ? diff : a.symbol.localeCompare(b.symbol);
      });

      const strategyRealized = toFiniteNumber(strategy.realized_pnl);
      const fallbackRealized = children.reduce((sum, child) => sum + child.realizedPnl, 0);
      const realizedPnl = strategyRealized ?? fallbackRealized;
      const costToTrade = children.reduce(
        (sum, child) => sum + (child.costToTrade ?? 0),
        0,
      );
      const qty = children.reduce((sum, child) => sum + child.qty, 0);
      const tradedAt =
        strategy.closed_at ??
        children[0]?.tradedAt ??
        strategy.updated_at ??
        strategy.created_at;

      return {
        strategyPositionId: strategy.strategy_position_id,
        strategyName: strategy.name,
        tradedAt,
        costToTrade,
        qty,
        realizedPnl,
        children,
      };
    })
    .filter((strategy) => strategy.children.length > 0 || strategy.realizedPnl !== 0)
    .sort((a, b) => {
      const diff = safeParseTime(b.tradedAt) - safeParseTime(a.tradedAt);
      return diff !== 0 ? diff : a.strategyName.localeCompare(b.strategyName);
    });

/* ── Inner component (assumes contexts are available) ──────────────── */

const HistoricalPnlsInner: React.FC = () => {
  const { userUuid, getToken, isAuthenticated } = useAuth();
  const { subscribeToOrderEvents, subscribeToPortfolioStateUpdates } = useOrderTicketNavigation();

  const [normalRows, setNormalRows] = useState<NormalHistoricalRow[]>([]);
  const [strategyGroups, setStrategyGroups] = useState<StrategyHistoricalGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStrategies, setExpandedStrategies] = useState<Record<string, boolean>>({});

  const latestPositionVersionRef = useRef<number>(0);
  const orderFillRefreshTimerRef = useRef<number | null>(null);

  const applyHistoricalPositionState = useCallback(
    (incoming: PositionStateSnapshot) => {
      if (incoming.user_uuid !== userUuid) return;

      const nextVersion =
        typeof incoming.version === 'number' && Number.isFinite(incoming.version)
          ? incoming.version
          : null;
      if (nextVersion != null && nextVersion < latestPositionVersionRef.current) return;
      if (nextVersion != null) latestPositionVersionRef.current = nextVersion;

      setNormalRows(buildNormalRows(Object.values(incoming.normal_positions ?? {})));
      setStrategyGroups(
        buildStrategyGroups(Object.values(incoming.strategy_positions ?? {})),
      );
    },
    [userUuid],
  );

  const refreshHistoricalState = useCallback(async () => {
    if (!isAuthenticated || !userUuid) return;
    try {
      const token = await getToken();
      if (!token) return;
      const latest = await tradingService.getPositionStateWithOptions(userUuid, token, {
        includeClosed: true,
      });
      applyHistoricalPositionState(latest);
    } catch {
      // Best-effort refresh for resiliency after fills.
    }
  }, [applyHistoricalPositionState, getToken, isAuthenticated, userUuid]);

  const scheduleHistoricalRefresh = useCallback(() => {
    if (orderFillRefreshTimerRef.current != null) {
      window.clearTimeout(orderFillRefreshTimerRef.current);
    }
    orderFillRefreshTimerRef.current = window.setTimeout(() => {
      orderFillRefreshTimerRef.current = null;
      void refreshHistoricalState();
    }, 150);
  }, [refreshHistoricalState]);

  useEffect(() => {
    latestPositionVersionRef.current = 0;
  }, [userUuid]);

  useEffect(
    () => () => {
      if (orderFillRefreshTimerRef.current != null) {
        window.clearTimeout(orderFillRefreshTimerRef.current);
        orderFillRefreshTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated || !userUuid) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const positionState = await tradingService.getPositionStateWithOptions(userUuid, token, {
          includeClosed: true,
        });
        if (!cancelled) applyHistoricalPositionState(positionState);
      } catch {
        if (!cancelled) setError('Failed to load historical PnLs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [applyHistoricalPositionState, getToken, isAuthenticated, userUuid]);

  useEffect(
    () =>
      subscribeToOrderEvents((event) => {
        if (event.event_type !== 'filled') return;
        scheduleHistoricalRefresh();
      }),
    [scheduleHistoricalRefresh, subscribeToOrderEvents],
  );

  useEffect(
    () =>
      subscribeToPortfolioStateUpdates(() => {
        scheduleHistoricalRefresh();
      }),
    [scheduleHistoricalRefresh, subscribeToPortfolioStateUpdates],
  );

  const toggleStrategyExpanded = useCallback((strategyPositionId: string) => {
    setExpandedStrategies((prev) => ({
      ...prev,
      [strategyPositionId]: !prev[strategyPositionId],
    }));
  }, []);

  const rows = useMemo(
    () =>
      [
        ...normalRows.map((row) => ({
          kind: 'normal' as const,
          tradedAt: row.tradedAt,
          row,
        })),
        ...strategyGroups.map((row) => ({
          kind: 'strategy' as const,
          tradedAt: row.tradedAt,
          row,
        })),
      ].sort((a, b) => {
        const diff = safeParseTime(b.tradedAt) - safeParseTime(a.tradedAt);
        if (diff !== 0) return diff;
        if (a.kind === b.kind) return 0;
        return a.kind === 'strategy' ? -1 : 1;
      }),
    [normalRows, strategyGroups],
  );

  const totalRows = normalRows.length + strategyGroups.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        color: 'var(--tr-text-primary, #e2e8f0)',
      }}
    >
      <div
        className={WIDGET_DRAG_HANDLE_CLASS}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '6px 10px',
          background: 'var(--tr-bg-secondary, rgba(255,255,255,0.02))',
          borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
          fontSize: '0.8125rem',
          fontWeight: 700,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <span>
          Historical Pnls
          {!loading && (
            <span style={{ fontSize: '0.75rem', color: 'var(--tr-text-muted)', marginLeft: '6px' }}>
              ({totalRows})
            </span>
          )}
        </span>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 }}>
          Loading…
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', color: 'var(--tr-pnl-negative, #ef4444)', fontSize: '0.875rem', flex: 1 }}>
          {error}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <table className={compactStyles.compactTable}>
            <thead>
              <tr>
                <th>Traded At</th>
                <th>Symbol</th>
                <th className={compactStyles.numericCell}>Cost to Trade</th>
                <th className={compactStyles.numericCell}>Qty</th>
                <th className={compactStyles.numericCell}>Realized PnL</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--tr-text-muted)', padding: '16px' }}>
                    No realized PnL records yet
                  </td>
                </tr>
              )}

              {rows.map((entry) => {
                if (entry.kind === 'normal') {
                  const row = entry.row;
                  return (
                    <tr key={`normal:${row.key}`}>
                      <td>{fmtTime(row.tradedAt)}</td>
                      <td>
                        <span className={compactStyles.symbolCellContent}>
                          <SymbolLogo symbol={row.symbol} size={16} />
                          <span className={compactStyles.symbolCellLabel}>{row.symbol}</span>
                        </span>
                      </td>
                      <td className={compactStyles.numericCell}>{fmtCurrency(row.costToTrade)}</td>
                      <td className={compactStyles.numericCell}>{fmtQty(row.qty)}</td>
                      <td className={`${compactStyles.numericCell} ${pnlClass(row.realizedPnl)}`}>
                        {fmtCurrency(row.realizedPnl, true)}
                      </td>
                    </tr>
                  );
                }

                const strategy = entry.row;
                const isExpanded = !!expandedStrategies[strategy.strategyPositionId];
                const hasChildren = strategy.children.length > 0;
                return (
                  <React.Fragment key={`strategy:${strategy.strategyPositionId}`}>
                    <tr
                      style={{
                        cursor: hasChildren ? 'pointer' : 'default',
                        background: 'rgba(255,255,255,0.04)',
                      }}
                      onClick={
                        hasChildren
                          ? () => toggleStrategyExpanded(strategy.strategyPositionId)
                          : undefined
                      }
                    >
                      <td>{fmtTime(strategy.tradedAt)}</td>
                      <td>
                        <span className={compactStyles.symbolCellContent}>
                          {hasChildren && (
                            <span style={{ width: '14px', textAlign: 'center' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          )}
                          <span className={compactStyles.symbolCellLabel}>
                            {strategy.strategyName}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--tr-text-muted)' }}>
                            Strategy
                          </span>
                        </span>
                      </td>
                      <td className={compactStyles.numericCell}>{fmtCurrency(strategy.costToTrade)}</td>
                      <td className={compactStyles.numericCell}>{fmtQty(strategy.qty)}</td>
                      <td className={`${compactStyles.numericCell} ${pnlClass(strategy.realizedPnl)}`}>
                        {fmtCurrency(strategy.realizedPnl, true)}
                      </td>
                    </tr>
                    {hasChildren &&
                      isExpanded &&
                      strategy.children.map((child) => (
                        <tr
                          key={`strategy-child:${child.key}`}
                          style={{ background: 'rgba(255,255,255,0.01)' }}
                        >
                          <td>{fmtTime(child.tradedAt)}</td>
                          <td>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                paddingLeft: '24px',
                              }}
                            >
                              <span style={{ fontSize: '0.72rem', color: 'var(--tr-text-muted)' }}>
                                {child.legId}
                              </span>
                              <span>{child.symbol}</span>
                            </span>
                          </td>
                          <td className={compactStyles.numericCell}>
                            {fmtCurrency(child.costToTrade)}
                          </td>
                          <td className={compactStyles.numericCell}>{fmtQty(child.qty)}</td>
                          <td className={`${compactStyles.numericCell} ${pnlClass(child.realizedPnl)}`}>
                            {fmtCurrency(child.realizedPnl, true)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Error boundary to catch missing context ───────────────────────── */

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class HistoricalPnlsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error in Historical Pnls widget.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('HistoricalPnlsWidget render error:', error);
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.message;
      let displayMessage = msg || 'Unable to render Historical Pnls widget.';

      if (msg.includes('OrderTicketNavigationProvider')) {
        displayMessage =
          'OrderTicketNavigationContext is not available. Ensure this widget is rendered inside an OrderTicketNavigationProvider.';
      } else if (msg.includes('AuthProvider')) {
        displayMessage =
          'AuthContext is not available. Ensure this widget is rendered inside an AuthProvider.';
      }

      return (
        <div style={{ padding: '1rem', color: 'var(--color-error, #e74c3c)' }}>
          <strong>Historical Pnls unavailable</strong>
          <p style={{ marginTop: '0.5rem' }}>{displayMessage}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Self-contained Historical Pnls widget.
 *
 * Loads historical position state from TradingService, filters for realized
 * rows where closed quantity is non-zero, renders normal rows and collapsible
 * strategy rows, and color-codes realized PnL values.
 *
 * This widget is intentionally non-ticking and refreshes only when fills or
 * portfolio state updates occur.
 */
export const HistoricalPnlsWidget: React.FC<WidgetInstanceProps> = (_props) => {
  return (
    <HistoricalPnlsErrorBoundary>
      <HistoricalPnlsInner />
    </HistoricalPnlsErrorBoundary>
  );
};

export default HistoricalPnlsWidget;
